import React, { useState, useEffect } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import Users from './users'
import Vehicles from './vehicles/vehicles'
import './fleet.css'
import { dismissAjaxError } from '../../state/actions/app'
import MainPage from '../mainPage'
import { Teams } from './teams/teams'
import { useAuth0 } from '@auth0/auth0-react'
import { addChildNode } from '../../state/actions/customer'
import { CustomModal } from '@modul-connect/shared/components/molecules/modal/customModal'
import { useNavigate } from 'react-router-dom'
import { AssetOverview } from './assetOverview/assetOverview'

const Fleet = ({
  vehicles_sorted,
  vehicles,
  nodes,
  total,
  userTags
}) => {
  const [wordReplacements, setWordReplacements] = useState(null)
  const [orgSubPages, setOrgSubPages] = useState([])
  const [openModal, setOpenModal] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [teamExists, setTeamExists] = useState(false)
  const [teamName, setTeamName] = useState(undefined)

  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const dispatch = useDispatch()

  const saving = useSelector(state => state.saving)

  const isAdmin = userTags.length && userTags.find(x => ['admin'].includes(x.privilege))

  useEffect(() => {
    const vehicleNames = [
      ...(vehicles_sorted.data ?? []),
      ...(vehicles ?? []),
    ].map((vehicle) => ({
      word: vehicle.mainbox_id,
      replacement: vehicle.vehicleName ?? vehicle.vehicle?.baseVehicleName,
    }))
    setWordReplacements([...vehicleNames])
  }, [vehicles_sorted, vehicles])

  useEffect(() => {
    if (!nodes?.length) {
      setOrgSubPages([])
      return
    }

    const dynamicOrgSubPages = nodes
      .map((org) => ({
        id: org.uuid,
        title: org.name,
        children: org.children
      }))
      .filter(o =>
        userTags.find(u => u.tags == o.id && ['super', 'admin'].includes(u.privilege))
      )

    const sortedOrganisations = [...dynamicOrgSubPages].sort((a, b) => {
      return a.title < b.title ? -1 : a.title > b.title ? 1 : 0
    })

    const organisationRows = sortedOrganisations.flatMap(org => {
      const updatedOrg = org
      updatedOrg.children = updatedOrg.children ?? []
      return ([
        { id: updatedOrg.id, title: updatedOrg.title, disabled: true },
        ...updatedOrg.children.map(child => ({
          id: child.uuid,
          title: child.name,
          parentId: updatedOrg.id,
        })),
      ])
    })

    setOrgSubPages(organisationRows)
  }, [total, nodes])

  const roots = orgSubPages
    .filter(row => row.disabled)
    .map(root => ({ id: root.id, title: root.title }))

  const children = orgSubPages
    .filter(row => !row.disabled)
    .map(team => ({ ...team }))

  const addTeam = (newTeam) => {
    const name = newTeam?.name                 
 
    const org = nodes.find(o => o.uuid === selectedOrg)
    if (!org) return
  
    const exists = org.children?.some(
      (child) => (child?.name || '').trim().toLowerCase() === name.toLowerCase()
    )

    setTeamName(name.trim())

    if (exists) {
      setTeamExists(true)
      return
    }
  
    getAccessTokenSilently()
      .then(token => {
        dispatch(addChildNode(token, selectedOrg, newTeam.name))
      })
    setTeamExists(false)
  }

  useEffect(() => {
    if (saving && saving.addChildNode === null) {
      setOpenModal(false)
  
      if (teamName && selectedOrg && Array.isArray(nodes)) {
        const org = nodes.find(o => o.uuid === selectedOrg)
        const match = org?.children?.find(
          c => (c?.name || '').trim().toLowerCase() === teamName.toLowerCase()
        )
        if (match?.uuid) {
          navigate(`/fleet/fleet_teams/${match.uuid}`, { state: { skipFetch: true } })
        }
        setTeamName(null)
      }
    }
  }, [saving, nodes]) 

  const sidebarContent = [
    {
      id: 'assetOverview',
      title: 'Asset Overview',
      content: <AssetOverview />,
      icon: 'device',
      subPages: [{ id: 'assetOverview', title: 'Maintenance' }],
      // hide: true
    },
    {
      id: 'vehicles',
      title: 'Vehicles',
      content: <Vehicles />,
      icon: 'MdDirectionsCar',
      subPages: [{ id: 'main', title: 'Vehicles' }],
    },
    {
      id: 'users',
      title: 'Users',
      content: <Users />,
      icon: 'MdSupervisorAccount',
      subPages: [{ id: 'users', title: 'Users' }],
    },
  ]

  if (isAdmin) {
    const teamNodes = roots.flatMap(root => ([
      { type: 'subtitle', text: root.title },
      ...children
        .filter(c => c.parentId === root.id)
        .map(c => ({ type: 'item', id: c.id, title: c.title })),
      { type: 'action', text: '+ Create team', onClick: () => {
        setSelectedOrg(root.id)
        setOpenModal(true)
      }
      },
      { type: 'divider' },
      { type: 'item', id: `all-users-${root.id}`, title: 'All users', onClick: undefined },
      { type: 'item', id: `all-vehicles-${root.id}`, title: 'All vehicles', onClick: undefined },
      { type: 'spacing' },
    ]))

    sidebarContent.unshift({
      id: 'teams',
      title: 'Teams',
      content: <Teams orgSubPages={orgSubPages}/>,
      icon: 'MdGroups',
      subPages: children,
      nodes: teamNodes
    })
  }

  return (
    <>
      <MainPage
        rootURL={'fleet'}
        landingPageURL={'fleet/fleet_teams'}
        initialPageIndex={0} // must match your routes default
        hideBreadcrumbs={true}
        breadCrumbWordReplacements={wordReplacements}
        sidebarContent={sidebarContent}
      />
      
      {openModal && (
        <CustomModal
          openModal={openModal}
          onClose={() => {
            setOpenModal(false)
            setTeamExists(false)
          }}
          addTeam={addTeam}
          orgname={selectedOrg?.name}
          disabled={saving.addChildNode === 'saving'}
          addingFailed={saving.addChildNode === 'failed'}
          teamAlreadyExist={teamExists}

        />
      )}
    </>
  )
}

const mapStateToProps = ({ vehicles_sorted, vehicles, subtrees, authenticated_user }) => ({
  vehicles_sorted,
  vehicles,
  nodes: subtrees.subtrees,
  total: subtrees.totalChildren,
  userTags: authenticated_user?.tags || []
})

const mapDispatchToProps = (dispatch) => ({
  dismissAjaxError: () => dispatch(dismissAjaxError()),
})

export default connect(mapStateToProps, mapDispatchToProps)(Fleet)